import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Phone, Clock, Share2, UserCircle2, Megaphone, Star, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, imgSrc } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PublicHeader, PublicFooter, TierBadge, DistanceChip, RecencyChip, RewardOffer, StarRating, Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OwnerEditButton, YourBusinessPill } from "@/components/OwnerEdit";
import { PausedRewardNotice } from "@/components/PausedRewardNotice";
import { TrustBadge } from "@/components/ReferralFeedback";

export const BranchProfileView = ({ branch, referrerName, children, afterDetails, hideInactiveReward = false, rewardNotice = null }) => {
  const [referrers, setReferrers] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [ratings, setRatings] = useState({ ratings: [], average: null, count: 0 });
  useEffect(() => {
    api.get(`/branches/${branch.id}/referrers`).then(({ data }) => setReferrers(data)).catch(() => {});
    api.get(`/branches/${branch.id}/updates`).then(({ data }) => setUpdates(data)).catch(() => {});
    api.get(`/branches/${branch.id}/ratings`).then(({ data }) => setRatings(data)).catch(() => {});
  }, [branch.id]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      {branch.portfolio_image_urls?.[0] && (
        <div className="mb-6 h-56 w-full overflow-hidden rounded-[16px]">
          <img src={imgSrc(branch.portfolio_image_urls[0])} alt={branch.company_name} className="h-full w-full object-cover" />
        </div>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-3xl font-black tracking-tight" data-testid="branch-name">{branch.company_name}</h1>
            {branch.subcategory_badge && (
              <span className="rounded-full bg-brand-green/10 px-2.5 py-0.5 text-xs font-semibold text-brand-green-dark dark:text-brand-green" data-testid="branch-sub-badge">
                {branch.subcategory_badge}
              </span>
            )}
            <YourBusinessPill branch={branch} />
          </div>
          {branch.label && <p className="text-muted-foreground">{branch.label}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <TierBadge tier={branch.payer_tier} />
            <DistanceChip miles={branch.distance_mi} />
            <RecencyChip recency={branch.last_payout_recency} />
            {ratings.average != null && (
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-gold-dark dark:text-brand-gold" data-testid="branch-avg-rating">
                <Star className="h-3.5 w-3.5 fill-brand-gold text-brand-gold" />{ratings.average} ({ratings.count})
              </span>
            )}
          </div>
        </div>
        {branch.is_owner && (
          <OwnerEditButton branch={branch} withLabel />
        )}
      </div>

      {branch.about && <p className="mt-6 leading-relaxed text-muted-foreground">{branch.about}</p>}

      <div className="mt-6">
        {branch.reward_active ? (
          <RewardOffer reward={branch.reward} referrerName={referrerName} />
        ) : rewardNotice ? (
          rewardNotice
        ) : hideInactiveReward ? null : (
          <div className="rounded-[14px] border border-dashed border-border p-5 text-muted-foreground" data-testid="no-reward">
            {branch.verification_status !== "verified"
              ? "This business hasn't claimed its profile yet — no reward is available."
              : "The reward offer is currently paused."}
          </div>
        )}
      </div>

      {children}

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-[14px] border border-border bg-card p-5">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Details</p>
          {branch.address && <p className="flex items-center gap-2 text-sm"><span className="text-muted-foreground">📍</span>{branch.address}</p>}
          {branch.phone && <p className="mt-2 flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-muted-foreground" />{branch.phone}</p>}
          <div className="mt-2 flex flex-wrap gap-1">
            {(branch.service_modes || []).map((m) => (
              <span key={m} className="rounded-full bg-secondary px-2 py-0.5 text-xs">{m.replace("_", " ")}</span>
            ))}
          </div>
        </div>
        <div className="rounded-[14px] border border-border bg-card p-5">
          <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-green"><Clock className="h-3.5 w-3.5" /> Hours</p>
          {branch.hours && Object.keys(branch.hours).length ? (
            <ul className="text-sm text-muted-foreground">
              {Object.entries(branch.hours).map(([d, h]) => (
                <li key={d} className="flex justify-between"><span className="capitalize">{d}</span><span>{h}</span></li>
              ))}
            </ul>
          ) : <p className="text-sm text-muted-foreground">Not listed</p>}
        </div>
      </div>

      {afterDetails}

      <div className="mt-8">
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Referred by</p>
        {referrers.length === 0 ? (
          <p className="text-sm text-muted-foreground">Be the first to refer this business.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {referrers.slice(0, 8).map((r) => (
              <div key={r.user_id} className="flex items-center gap-3 rounded-[10px] border border-border bg-card px-4 py-2">
                <UserCircle2 className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">{r.name}</span>
                <TrustBadge badge={r.trust_badge} />
                {r.is_contact && <span className="rounded-full bg-brand-green/15 px-2 py-0.5 text-xs text-brand-green">contact</span>}
                <span className="ml-auto text-sm text-muted-foreground">{r.referral_count} referral{r.referral_count > 1 ? "s" : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {updates.length > 0 && (
        <div className="mt-8" data-testid="updates-section">
          <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-green"><Megaphone className="h-3.5 w-3.5" /> Latest updates</p>
          <div className="flex flex-col gap-3">
            {updates.map((u) => (
              <div key={u.id} className="rounded-[12px] border border-border bg-card p-4" data-testid={`public-update-${u.id}`}>
                <h3 className="font-semibold">{u.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{u.body}</p>
                {u.image_url && <img src={imgSrc(u.image_url)} alt={u.title} className="mt-3 max-h-56 rounded-[10px] object-cover" />}
              </div>
            ))}
          </div>
        </div>
      )}

      {ratings.ratings.length > 0 && (
        <div className="mt-8" data-testid="public-ratings-section">
          <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-green"><Star className="h-3.5 w-3.5" /> Ratings</p>
          <div className="flex flex-col gap-3">
            {ratings.ratings.filter((r) => r.revealed).map((r) => (
              <div key={r.id} className="rounded-[12px] border border-border bg-card p-4">
                <StarRating value={r.rating} size={18} readOnly />
                {r.owner_response && <p className="mt-2 rounded-[8px] bg-secondary p-2 text-sm"><span className="font-semibold text-brand-green">Owner:</span> {r.owner_response}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Claim CTA for unclaimed businesses — the landing target of snap-a-business
// outreach links ({origin}/b/{branch_id}). Reuses the existing claim/verify flow:
// POST /branches/{id}/claim sends a code to the branch's number, then
// POST /branches/{id}/verify confirms ownership.
const ClaimBusiness = ({ branch, onClaimed }) => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [stage, setStage] = useState("idle"); // idle | pending | done
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);

  const startClaim = async () => {
    if (!user) { nav(`/login?redirect=/b/${branch.id}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/branches/${branch.id}/claim`);
      setSentTo(data.sent_to || branch.phone || "the number on file");
      if (data.dev_verification_code) setCode(data.dev_verification_code);
      setStage("pending");
      toast.success("We texted a verification code to the business number.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post(`/branches/${branch.id}/verify`, { code: code.trim() });
      setStage("done");
      toast.success("Verified! You now own this business.");
      onClaimed?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (stage === "done") return null;

  return (
    <div className="mt-6 rounded-[14px] border border-brand-green/30 bg-brand-green/5 p-5" data-testid="claim-business">
      <div className="flex items-center gap-2 text-brand-green">
        <BadgeCheck className="h-5 w-5" />
        <p className="font-bold">Own this business?</p>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Claim your free profile to set a referral reward and manage your listing. We'll send a
        verification code to the business phone number on file.
      </p>
      {stage === "idle" ? (
        <Button className="mt-3 bg-brand-green hover:bg-brand-green-dark" disabled={busy} onClick={startClaim} data-testid="claim-start-btn">
          {busy ? "Sending…" : "Claim this business"}
        </Button>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Code sent to {sentTo}</span>
          <Input className="w-40" placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="claim-code-input" />
          <Button className="bg-brand-green hover:bg-brand-green-dark" disabled={busy || !code.trim()} onClick={verify} data-testid="claim-verify-btn">
            {busy ? "Verifying…" : "Verify"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default function BranchProfile() {
  const { branchId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [branch, setBranch] = useState(null);

  const reload = () => api.get(`/branches/${branchId}`).then(({ data }) => setBranch(data)).catch(() => setBranch(false));

  useEffect(() => { reload(); }, [branchId]);

  if (branch === null) return (<div className="flex min-h-screen flex-col"><PublicHeader /><Loading /><PublicFooter /></div>);
  if (branch === false) return (<div className="flex min-h-screen flex-col"><PublicHeader /><p className="flex-1 p-10 text-center text-muted-foreground">Branch not found.</p><PublicFooter /></div>);

  const showReferrerPause =
    user &&
    !branch.is_owner &&
    !branch.reward_active &&
    branch.verification_status === "verified";

  const referPath = `/app/refer/${branch.id}`;
  const startRefer = () => {
    if (user) nav(referPath);
    else nav(`/login?redirect=${encodeURIComponent(referPath)}`);
  };

  return (
    <div className="flex min-h-screen flex-col">
      <PublicHeader />
      <BranchProfileView
        branch={branch}
        rewardNotice={
          showReferrerPause ? (
            <PausedRewardNotice branch={branch} onUpdated={setBranch} />
          ) : null
        }
      >
        {!branch.is_owner && (
          <Button
            className={
              branch.reward_active
                ? "mt-5 w-full bg-brand-gold text-brand-ink hover:bg-brand-gold-dark sm:w-auto"
                : "mt-5 w-full sm:w-auto"
            }
            variant={branch.reward_active ? "default" : "outline"}
            onClick={startRefer}
            data-testid="refer-this-business-btn"
          >
            <Share2 className="mr-2 h-4 w-4" /> Know someone who needs this? Refer them
          </Button>
        )}
        {branch.verification_status !== "verified" && (
          <ClaimBusiness branch={branch} onClaimed={reload} />
        )}
      </BranchProfileView>
      <PublicFooter />
    </div>
  );
}
